import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { projects, discovery } from '@/api/client'
import {
  ArrowLeft, ArrowRight, RotateCcw, X, Users, Building, Globe, AlertTriangle,
  CheckCircle2, ShieldCheck, Trash2, Loader2
} from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { DiscoveryProgressBar } from '@/components/DiscoveryProgressBar'

type Tab = 'overview' | 'users' | 'groups' | 'domains' | 'conflicts'

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const projectQuery = useQuery({
    queryKey: ['project', id],
    queryFn: () => projects.get(id!),
    refetchInterval: 3000,
    enabled: !!id,
  })

  const job = projectQuery.data?.latestDiscovery
  const isJobActive = job?.status === 'Running' || job?.status === 'Scheduled'

  const cancelJob = useMutation({
    mutationFn: () => discovery.cancel(id!, job!.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project', id] }),
  })

  const rerun = useMutation({
    mutationFn: () => discovery.rerun(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project', id] }),
  })

  const deleteProject = useMutation({
    mutationFn: () => projects.delete(id!, confirmText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate('/projects')
    },
  })

  if (projectQuery.isLoading) return <div className="text-slate-500">Carregando...</div>
  if (!projectQuery.data) return <div>Projeto não encontrado</div>
  const p = projectQuery.data

  return (
    <div>
      <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{p.name}</h1>
          {p.description && <p className="text-sm text-slate-600 mt-1">{p.description}</p>}
          <div className="flex items-center gap-2 mt-3 text-sm text-slate-600">
            <span className="font-medium">{p.sourceTenantDisplayName}</span>
            <ArrowRight className="h-3 w-3 text-slate-400" />
            <span className="font-medium">{p.destinationTenantDisplayName}</span>
            <span className="text-slate-300">•</span>
            <span>Domínio <strong className="text-slate-700">{p.domain}</strong></span>
            <span className="text-slate-300">•</span>
            <span>{p.scopeType === 'WholeDomain' ? 'Todo o domínio' : `${p.scopeUpns?.length || 0} UPNs`}</span>
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          {isJobActive && (
            <button onClick={() => cancelJob.mutate()} className="btn-secondary text-sm">
              <X className="h-4 w-4" /> Cancelar Discovery
            </button>
          )}
          {!isJobActive && (
            <>
              <button onClick={() => rerun.mutate()} className="btn-secondary text-sm">
                <RotateCcw className="h-4 w-4" /> Rodar Discovery novamente
              </button>
              {(p.status === 'Discovering' || p.status === 'Planning' || p.status === 'Draft') && (
                <Link to={`/projects/${id}/plan`} className="btn-primary text-sm">
                  Planejar migração →
                </Link>
              )}
              {(p.status === 'Approved' || p.status === 'Executing' || p.status === 'Completed') && (
                <Link to={`/projects/${id}/review`} className="btn-secondary text-sm">
                  Ver plano →
                </Link>
              )}
              {(p.status === 'Approved' || p.status === 'Executing') && (
                <Link to={`/projects/${id}/execute`} className="btn-primary text-sm">
                  Executar →
                </Link>
              )}
              <button
                onClick={() => { setShowDeleteModal(true); setConfirmText(''); deleteProject.reset() }}
                className="btn-secondary text-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Excluir projeto e todos os dados"
              >
                <Trash2 className="h-4 w-4" /> Excluir
              </button>
            </>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !deleteProject.isPending && setShowDeleteModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="rounded-full bg-red-100 p-2">
                <Trash2 className="h-4 w-4 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Excluir projeto</h2>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Esta ação <strong>não pode ser desfeita</strong>. Tudo será apagado:
            </p>
            <ul className="text-xs text-slate-600 mb-4 list-disc ml-5 space-y-0.5">
              <li>Plano e todos os mapeamentos de usuário</li>
              <li>Ondas, tarefas e checkpoints</li>
              <li>Resultados de Discovery (usuários, grupos, domínios descobertos)</li>
              <li>Snapshots de licenças do destino</li>
              <li>Relatórios de reconciliação</li>
            </ul>

            <p className="text-sm text-slate-600 mb-2">
              Para confirmar, digite o nome do projeto:
            </p>
            <p className="text-xs font-mono bg-slate-100 px-2 py-1 rounded border border-slate-200 mb-3 break-all">
              {p.name}
            </p>

            <input
              type="text"
              className="input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Digite o nome aqui"
              disabled={deleteProject.isPending}
              autoFocus
            />

            {deleteProject.isError && (
              <div className="rounded-md bg-red-50 border border-red-200 p-2 mt-3 text-xs text-red-800">
                {(deleteProject.error as any)?.data?.error || (deleteProject.error as any)?.message || 'Erro ao excluir'}
              </div>
            )}

            <div className="flex gap-2 mt-5 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteProject.isPending}
                className="btn-secondary text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={() => deleteProject.mutate()}
                disabled={confirmText !== p.name || deleteProject.isPending}
                className="btn-danger text-sm"
              >
                {deleteProject.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Excluir permanentemente
              </button>
            </div>
          </div>
        </div>
      )}

      {job && (
        <div className="mb-6">
          <DiscoveryProgressBar job={job} />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-6">
          {(['overview', 'users', 'groups', 'domains', 'conflicts'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={
                'py-3 text-sm font-medium border-b-2 transition-colors ' +
                (tab === t
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700')
              }
            >
              {t === 'overview' && 'Visão geral'}
              {t === 'users' && 'Usuários'}
              {t === 'groups' && 'Grupos'}
              {t === 'domains' && 'Domínios'}
              {t === 'conflicts' && 'Conflitos'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && <OverviewTab projectId={id!} />}
      {tab === 'users' && <UsersTab projectId={id!} />}
      {tab === 'groups' && <GroupsTab projectId={id!} />}
      {tab === 'domains' && <DomainsTab projectId={id!} />}
      {tab === 'conflicts' && <UsersTab projectId={id!} conflictsOnly />}
    </div>
  )
}

function OverviewTab({ projectId }: { projectId: string }) {
  const summary = useQuery({
    queryKey: ['discovery-summary', projectId],
    queryFn: () => discovery.summary(projectId),
    retry: false,
  })

  if (summary.isLoading) return <div className="text-slate-500">Carregando...</div>
  if (summary.isError) return (
    <div className="card p-6 text-center text-slate-500">
      Discovery ainda não foi concluído. Aguarde...
    </div>
  )

  const s = summary.data
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard icon={Users} label="Usuários (origem)" value={s.totalUsers} />
      <StatCard icon={CheckCircle2} label="No escopo" value={s.usersInScope} highlight />
      <StatCard icon={ShieldCheck} label="Licenciados" value={s.licensedUsers} />
      <StatCard icon={AlertTriangle} label="Conflitos" value={s.conflicts} alert={s.conflicts > 0} />
      <StatCard icon={Users} label="Fora do escopo" value={s.usersOutOfScope} />
      <StatCard icon={Building} label="Grupos" value={s.groups} />
      <StatCard icon={Globe} label="Domínios" value={s.domains} />
      <StatCard icon={CheckCircle2} label="Domínios verificados" value={s.verifiedDomains} />
    </div>
  )
}

function StatCard({ icon: Icon, label, value, highlight, alert }: any) {
  return (
    <div className={
      'card p-4 ' +
      (alert ? 'border-red-200 bg-red-50' : highlight ? 'border-brand-200 bg-brand-50' : '')
    }>
      <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
        <Icon className="h-4 w-4" />
        <span>{label}</span>
      </div>
      <div className={'text-2xl font-semibold ' + (alert ? 'text-red-700' : highlight ? 'text-brand-700' : 'text-slate-900')}>
        {value}
      </div>
    </div>
  )
}

function UsersTab({ projectId, conflictsOnly }: { projectId: string; conflictsOnly?: boolean }) {
  const [side, setSide] = useState('Source')
  const [inScopeOnly, setInScopeOnly] = useState(true)
  const usersQuery = useQuery({
    queryKey: ['discovered-users', projectId, side, inScopeOnly, conflictsOnly],
    queryFn: () => discovery.users(projectId, { side, inScopeOnly, conflictsOnly, take: 100 }),
  })

  if (usersQuery.isLoading) return <div className="text-slate-500">Carregando...</div>
  const items = usersQuery.data?.items || []

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        {!conflictsOnly && (
          <>
            <select className="input max-w-xs" value={side} onChange={(e) => setSide(e.target.value)}>
              <option value="Source">Tenant origem</option>
              <option value="Destination">Tenant destino</option>
            </select>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={inScopeOnly} onChange={(e) => setInScopeOnly(e.target.checked)} />
              Apenas no escopo
            </label>
          </>
        )}
        <span className="text-sm text-slate-500 ml-auto">
          {usersQuery.data?.total || 0} resultados
        </span>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase text-slate-500">
              <th className="px-4 py-2 font-medium">UPN</th>
              <th className="px-4 py-2 font-medium">Nome</th>
              <th className="px-4 py-2 font-medium">Cargo</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Licenças</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Nenhum usuário encontrado</td></tr>
            ) : items.map((u) => (
              <tr key={u.id} className={u.hasConflict ? 'bg-red-50' : ''}>
                <td className="px-4 py-2 font-mono text-xs">{u.userPrincipalName}</td>
                <td className="px-4 py-2">{u.displayName || '—'}</td>
                <td className="px-4 py-2 text-slate-600">{u.jobTitle || '—'}</td>
                <td className="px-4 py-2">
                  {u.accountEnabled ? (
                    <span className="badge-success">Ativo</span>
                  ) : (
                    <span className="badge-neutral">Desativado</span>
                  )}
                  {u.hasConflict && (
                    <span className="badge-error ml-1" title={u.conflictDescription}>Conflito</span>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-600">{u.assignedSkuIds?.length || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function GroupsTab({ projectId }: { projectId: string }) {
  const groupsQuery = useQuery({
    queryKey: ['discovered-groups', projectId],
    queryFn: () => discovery.groups(projectId, 'Source'),
  })

  if (groupsQuery.isLoading) return <div className="text-slate-500">Carregando...</div>
  const items = groupsQuery.data || []

  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-xs uppercase text-slate-500">
            <th className="px-4 py-2 font-medium">Nome</th>
            <th className="px-4 py-2 font-medium">Tipo</th>
            <th className="px-4 py-2 font-medium">Email</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.length === 0 ? (
            <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">Nenhum grupo encontrado</td></tr>
          ) : items.map((g) => (
            <tr key={g.id}>
              <td className="px-4 py-2">{g.displayName}</td>
              <td className="px-4 py-2"><span className="badge-info">{g.groupType}</span></td>
              <td className="px-4 py-2 font-mono text-xs text-slate-600">{g.mail || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DomainsTab({ projectId }: { projectId: string }) {
  const sourceQuery = useQuery({ queryKey: ['discovered-domains', projectId, 'Source'], queryFn: () => discovery.domains(projectId, 'Source') })
  const destQuery = useQuery({ queryKey: ['discovered-domains', projectId, 'Destination'], queryFn: () => discovery.domains(projectId, 'Destination') })

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <DomainsList title="Tenant origem" data={sourceQuery.data || []} loading={sourceQuery.isLoading} />
      <DomainsList title="Tenant destino" data={destQuery.data || []} loading={destQuery.isLoading} />
    </div>
  )
}

function DomainsList({ title, data, loading }: any) {
  return (
    <div className="card p-4">
      <h3 className="font-semibold text-slate-900 mb-3">{title}</h3>
      {loading ? <div className="text-slate-500 text-sm">Carregando...</div> : (
        <ul className="space-y-2">
          {data.length === 0 && <li className="text-slate-400 text-sm">Nenhum domínio</li>}
          {data.map((d: any) => (
            <li key={d.id} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-slate-400" />
                <span className="font-mono text-xs">{d.name}</span>
                {d.isDefault && <span className="badge-info text-[10px]">padrão</span>}
              </div>
              {d.isVerified ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-slate-300" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
